import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { FaTrash, FaStar, FaCheck, FaTimes } from 'react-icons/fa';
import AdminLayout from '../../components/AdminLayout';

export default function AdminReviews() {
  const { t } = useTranslation();
  const { API } = useContext(AppContext);
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'approved'

  useEffect(() => {
    fetchReviews();
  }, [filter]);

  const fetchReviews = async () => {
    try {
      const endpoint = filter === 'all' 
        ? `${API}/admin/reviews/all`
        : `${API}/admin/reviews/all?status=${filter}`;
      
      const response = await axios.get(endpoint);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.post(`${API}/admin/reviews/${id}/approve`);
      toast.success('Avis approuvé avec succès');
      fetchReviews();
    } catch (error) {
      console.error('Error approving review:', error);
      toast.error('Erreur lors de l\'approbation de l\'avis');
    }
  };

  const handleReject = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir rejeter et supprimer définitivement cet avis ?')) {
      try {
        await axios.post(`${API}/admin/reviews/${id}/reject`);
        toast.success('Avis rejeté et supprimé');
        fetchReviews();
      } catch (error) {
        console.error('Error rejecting review:', error);
        toast.error('Erreur lors du rejet de l\'avis');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet avis ?')) {
      try {
        await axios.delete(`${API}/admin/reviews/${id}`);
        toast.success('Avis supprimé avec succès');
        fetchReviews();
      } catch (error) {
        console.error('Error deleting review:', error);
        toast.error('Erreur lors de la suppression de l\'avis');
      }
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold">En attente</span>,
      approved: <span className="px-3 py-1 bg-[#635BFF]/10 text-[#5248d9] rounded-full text-xs font-semibold">Approuvé</span>,
      rejected: <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">Rejeté</span>
    };
    return badges[status] || null;
  };

  return (
    <AdminLayout>
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-reviews">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold mb-4 text-[#0A2540]">⭐ Gestion des Avis</h1>
        <p className="text-gray-600 mb-8">Approuvez ou rejetez les avis soumis par les utilisateurs</p>

        {/* Filter Tabs */}
        <div className="flex gap-3 mb-6">
          <Button
            onClick={() => setFilter('all')}
            variant={filter === 'all' ? 'default' : 'outline'}
            className={filter === 'all' ? 'bg-[#0A2540]' : ''}
          >
            Tous ({reviews.length})
          </Button>
          <Button
            onClick={() => setFilter('pending')}
            variant={filter === 'pending' ? 'default' : 'outline'}
            className={filter === 'pending' ? 'bg-yellow-600' : ''}
          >
            En attente
          </Button>
          <Button
            onClick={() => setFilter('approved')}
            variant={filter === 'approved' ? 'default' : 'outline'}
            className={filter === 'approved' ? 'bg-[#635BFF]' : ''}
          >
            Approuvés
          </Button>
        </div>

        <div className="space-y-4">
          {reviews.map((review, index) => (
            <Card key={review.id} data-testid={`review-${index}`}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center mb-2 gap-3">
                      <span className="font-semibold">{review.user_name}</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <FaStar key={i} className={i < review.rating ? 'text-yellow-400' : 'text-gray-300'} />
                        ))}
                      </div>
                      {getStatusBadge(review.status)}
                    </div>
                    <p className="text-gray-600 mb-2">{review.comment}</p>
                    <div className="flex gap-4 text-sm text-gray-400">
                      <span>Produit: {review.product_id.substring(0, 8)}...</span>
                      <span>
                        {new Date(review.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    {review.status === 'pending' && (
                      <>
                        <Button 
                          size="sm" 
                          className="bg-[#635BFF] hover:bg-[#5248d9]"
                          onClick={() => handleApprove(review.id)}
                          data-testid={`approve-review-${index}`}
                        >
                          <FaCheck className="mr-1" />
                          Approuver
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleReject(review.id)}
                          data-testid={`reject-review-${index}`}
                        >
                          <FaTimes className="mr-1" />
                          Rejeter
                        </Button>
                      </>
                    )}
                    {review.status === 'approved' && (
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleDelete(review.id)}
                        data-testid={`delete-review-${index}`}
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {reviews.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">
              {filter === 'all' && 'Aucun avis'}
              {filter === 'pending' && 'Aucun avis en attente'}
              {filter === 'approved' && 'Aucun avis approuvé'}
            </p>
          </div>
        )}
      </div>
    </div>
    </AdminLayout>
  );
}